// lib/slides/helveticaText.ts
//
// ONE sanitizer for everything drawn into the exported PDF with the standard
// Helvetica (WinAnsi) font. It used to exist twice - once in the export route,
// once in the callout renderer - with the same doc comment and two different
// behaviours, so the very same "Transparency ≥ 75%" printed as ">= 75%" in
// the validation card and as "? 75%" in a callout bubble of the SAME PDF.

import { PDFFont } from 'pdf-lib';

/** Typographic characters a reviewer actually types that WinAnsi has no slot
 *  for, spelled the way the guideline documents write them. Without this a
 *  comment reading "Transparency ≥ 75%" printed as "Transparency ? 75%" on the
 *  deliverable — technically safe, and unreadable. */
const HELVETICA_TRANSLITERATIONS: ReadonlyArray<[RegExp, string]> = [
  [/[\u2265]/g, '>='],
  [/[\u2264]/g, '<='],
  [/[\u2260]/g, '!='],
  [/[\u2248]/g, '~'],
  [/[\u2192\u27A1]/g, '->'],
  [/[\u2190]/g, '<-'],
  [/[\u2191]/g, 'up'],
  [/[\u2193]/g, 'down'],
  [/[\u00D7]/g, 'x'],
  [/[\u2022\u25AA\u25CF]/g, '-'],
  [/[\u2713\u2714]/g, 'OK'],
  [/[\u2717\u2718\u2716]/g, 'X'],
  [/[\u00A0\u202F\u2009]/g, ' '],
  [/[\u2212]/g, '-'],
  [/[\u00B2]/g, '2'],
  [/[\u00B3]/g, '3'],
];

/** Strip characters Helvetica (WinAnsi) cannot encode so drawText never throws. */
export function sanitizeForHelvetica(text: string, font: Pick<PDFFont, 'widthOfTextAtSize'>): string {
  try {
    font.widthOfTextAtSize(text, 10);
    return text;
  } catch {
    let out = text;
    for (const [pattern, replacement] of HELVETICA_TRANSLITERATIONS) out = out.replace(pattern, replacement);
    return Array.from(out, character => {
      try {
        font.widthOfTextAtSize(character, 10);
        return character;
      } catch {
        return '?';
      }
    }).join('');
  }
}
